/*
 * /patient/$patientId (page)
 */
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
	Placeholder,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { MessageSquare } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { GeneralNoteList } from "../../features/generalnote/general-note-list"
import { PatientHeader } from "../../features/patient/patient-header"
import { TelemedicineChatPanel } from "../../components/telemedicine-chat-panel"
import { useAuth } from "@emr/auth/client"
import type { Patient } from "@emr/db/client"

export const Route = createFileRoute("/patient/$patientId")({
	component: PatientPage,
})

/* ─── Mock patient (used when DB returns nothing) ────────────────────────── */

const MOCK_PATIENT: Partial<Patient> = {
	firstName: "John",
	lastName: "Doe",
	dateOfBirth: new Date("1978-08-19") as any,
	bloodType: "O+",
	allergies: "Penicillin",
	emergencyContactName: "Jane Doe (Spouse)",
	emergencyContactPhone: "(506) 897-6116",
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PatientPage() {
	const { patientId } = Route.useParams()

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: notesData, isLoading: notesLoading } =
		trpc.generalNote.getAllByFilter.useQuery({
			where: { patientId, active: true },
		})
	const notes = notesData ?? []

	const displayPatient = patient ?? (MOCK_PATIENT as Patient)
	const isLoading = patientLoading || notesLoading

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<Tabs defaultValue="notes">
						<TabsList>
							<TabsTrigger value="notes">Notes</TabsTrigger>
							<TabsTrigger value="messages">Messages</TabsTrigger>
						</TabsList>

						<TabsContent value="notes" className="mt-4">
							<Card>
								<CardHeader>
									<CardTitle>Patient Notes</CardTitle>
								</CardHeader>
								<CardContent>
									<GeneralNoteList patientId={patientId} notes={notes} />
								</CardContent>
							</Card>
						</TabsContent>

						<TabsContent value="messages" className="mt-4">
							<PatientMessagesTab patientId={patientId} />
						</TabsContent>
					</Tabs>
				)}
			</Container>
		</Page>
	)
}

/* ─── Messages tab ──────────────────────────────────────────────────────── */

function PatientMessagesTab({ patientId }: { patientId: string }) {
	const { user } = useAuth()
	const utils = trpc.useUtils()
	const [creatingSession, setCreatingSession] = useState(false)

	// Find messaging sessions for this patient (reuse getAllMessagingSessions, filter client-side)
	const { data: allSessions = [], isLoading, refetch } =
		trpc.telemedicineChat.getAllMessagingSessions.useQuery()

	const patientSessions = (allSessions as any[]).filter(
		(s) => s.patientId === patientId,
	)

	// Use the most-recent active session, or the most recent overall
	const activeSession = patientSessions.find((s) => s.active) ?? patientSessions[0] ?? null

	const openConversation = trpc.telemedicine.openConversation.useMutation({
		onSuccess: () => {
			refetch()
			utils.telemedicineChat.getAllMessagingSessions.invalidate()
			toast.success("Conversation started")
			setCreatingSession(false)
		},
		onError: (e) => toast.error(e.message),
	})

	const closeConversation = trpc.telemedicine.closeConversation.useMutation({
		onSuccess: () => {
			refetch()
			utils.telemedicineChat.getAllMessagingSessions.invalidate()
			toast.success("Conversation closed")
		},
	})

	if (isLoading) return <Placeholder title="Loading messages…" />

	if (!activeSession) {
		return (
			<Card>
				<CardContent className="py-12 flex flex-col items-center gap-3">
					<div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
						<MessageSquare className="w-7 h-7 text-muted-foreground" />
					</div>
					<p className="text-sm font-medium">No messages yet</p>
					<p className="text-xs text-muted-foreground">
						Start a conversation — the patient will be able to reply from their portal.
					</p>
					<Button
						size="sm"
						onClick={() => {
							setCreatingSession(true)
							openConversation.mutate({ patientId })
						}}
						disabled={openConversation.isPending || creatingSession}
					>
						{openConversation.isPending ? "Opening…" : "Start Conversation"}
					</Button>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="overflow-hidden">
			<CardHeader className="py-3 px-4 flex flex-row items-center justify-between gap-2 border-b">
				<CardTitle className="text-sm font-medium">
					Chat
					{!activeSession.active && (
						<span className="ml-2 text-xs font-normal text-muted-foreground">(Closed)</span>
					)}
				</CardTitle>
				<div className="flex items-center gap-2">
					{activeSession.active ? (
						<Button
							size="sm"
							variant="ghost"
							className="h-7 text-xs text-muted-foreground"
							onClick={() => closeConversation.mutate({ sessionId: activeSession.id })}
							disabled={closeConversation.isPending}
						>
							Close conversation
						</Button>
					) : (
						<Button
							size="sm"
							variant="ghost"
							className="h-7 text-xs"
							onClick={() => openConversation.mutate({ patientId })}
							disabled={openConversation.isPending}
						>
							New conversation
						</Button>
					)}
				</div>
			</CardHeader>
			<div className="h-[480px]">
				<TelemedicineChatPanel
					sessionId={activeSession.id}
					myUserId={user?.id}
					className="h-full border-0"
				/>
			</div>
		</Card>
	)
}
